/**
 * 队员树展开 / 组织分组 / 筛选（从 Dashboard 拆出）
 */
import { ONLINE_TIMEOUT, DEFAULT_OFFLINE_TIMEOUT, PRESENCE_STATUS } from './constants';
import { getMemberPresenceStatus, isMemberOnlineNow } from './memberMarkers';
import { missionIdMatchesMember } from './deviceIdentity';
import { isBogusGpsCoordinate } from '../bogusGps';

export function getMemberExpandKey(member) {
  return member?.device_unique_id || member?.name;
}

export function areAllMembersExpanded(membersList = [], openMember = {}) {
  const list = membersList || [];
  return list.length > 0 && list.every((m) => !!openMember[getMemberExpandKey(m)]);
}

export function buildExpandStateForMembers(membersList = [], expand) {
  const next = {};
  (membersList || []).forEach((m) => {
    next[getMemberExpandKey(m)] = !!expand;
  });
  return next;
}

/** 按组织 → 小组分组 */
export function groupMembersByOrgAndGroup(members = []) {
  const orgMap = {};
  (members || []).forEach((m) => {
    const org = m.org_name || '未知组织';
    const group = m.group_name || '未知小组';
    if (!orgMap[org]) orgMap[org] = {};
    if (!orgMap[org][group]) orgMap[org][group] = [];
    orgMap[org][group].push(m);
  });
  return orgMap;
}

/**
 * 按时间 / 有效性 / 在线状态筛选队员
 */
export function filterDashboardMembers(members = [], options = {}) {
  const {
    startTime = '',
    endTime = '',
    hideInvalid = false,
    showOnlineOnly = false,
    searchQuery = '',
    missionMode = false,
    missionMemberIds = null,
    now = Date.now(),
    onlineTimeout = ONLINE_TIMEOUT,
    offlineTimeout = DEFAULT_OFFLINE_TIMEOUT,
    presenceById = null
  } = options;

  let filtered = members || [];

  const q = String(searchQuery || '').trim().toLowerCase();
  if (q) {
    filtered = filtered.filter((member) => {
      const name = String(member.name || '').toLowerCase();
      const org = String(member.org_name || '').toLowerCase();
      const group = String(member.group_name || '').toLowerCase();
      return name.includes(q) || org.includes(q) || group.includes(q);
    });
  }

  if (startTime || endTime) {
    filtered = filtered.filter((member) => {
      if (!member.last_seen) return false;
      const memberTime = new Date(member.last_seen);
      if (Number.isNaN(memberTime.getTime())) return false;
      const start = startTime ? new Date(startTime) : null;
      const end = endTime ? new Date(endTime) : null;
      if (start && memberTime < start) return false;
      if (end && memberTime > end) return false;
      return true;
    });
  }

  if (hideInvalid) {
    filtered = filtered.filter((member) => member.valid !== false);
  }

  if (showOnlineOnly && !missionMode) {
    // missionMode: keep offline mission members; only manual remove / end mission clears
    filtered = filtered.filter((member) => {
      const id = member.device_unique_id || member.name;
      const status = (presenceById && id && presenceById[id])
        || getMemberPresenceStatus(member, now, onlineTimeout, offlineTimeout);
      return status === PRESENCE_STATUS.ONLINE || status === PRESENCE_STATUS.TEMP_OFFLINE;
    });
  }


  if (missionMode) {
    const ids = missionMemberIds instanceof Set
      ? Array.from(missionMemberIds)
      : (Array.isArray(missionMemberIds) ? missionMemberIds : []);
    if (ids.length === 0) {
      return [];
    }
    filtered = filtered.filter((member) => ids.some((id) => missionIdMatchesMember(id, member)));
  }

  // 始终隐藏 MaxMind/伪定位占位点（如 37.751,-97.822），避免指挥台飞到美国空地
  filtered = filtered.filter((member) => {
    if (member.last_latitude == null || member.last_longitude == null) return true;
    return !isBogusGpsCoordinate(member.last_latitude, member.last_longitude);
  });

  return filtered;
}

/** 首次进入：组织/小组全开，在线队员展开 */
export function buildInitialMemberTreeExpandState(members = []) {
  const orgMap = groupMembersByOrgAndGroup(members);
  const openOrg = {};
  const openGroup = {};
  const openMember = {};

  Object.keys(orgMap).forEach((org) => {
    openOrg[org] = true;
    Object.keys(orgMap[org]).forEach((group) => {
      openGroup[group] = true;
    });
  });

  // 队员详情默认全部收起（在线也不自动展开）；需点姓名前小三角才展开
  // openMember 保持空对象 = 全部 collapsed

  return { openOrg, openGroup, openMember, orgMap };
}

/** 仅补充新出现的组织/小组为展开 */
export function mergeNewOrgGroupExpand(prevOpen, orgMap, level = 'org') {
  const next = { ...prevOpen };
  let changed = false;
  Object.keys(orgMap || {}).forEach((org) => {
    if (level === 'org') {
      if (!(org in next)) {
        next[org] = true;
        changed = true;
      }
      return;
    }
    Object.keys(orgMap[org] || {}).forEach((group) => {
      if (!(group in next)) {
        next[group] = true;
        changed = true;
      }
    });
  });
  return changed ? next : prevOpen;
}

export function collectOnlineMemberIds(members = [], now = Date.now(), timeout = ONLINE_TIMEOUT) {
  const ids = new Set();
  (members || []).forEach((member) => {
    if (getMemberPresenceStatus(member, now, timeout, Math.max(timeout + 1000, DEFAULT_OFFLINE_TIMEOUT))
      === PRESENCE_STATUS.ONLINE) {
      ids.add(member.device_unique_id || member.name);
    }
  });
  return ids;
}

/** @returns {Map<string, 'online'|'temp_offline'|'offline'>} */
export function collectMemberPresenceMap(
  members = [],
  now = Date.now(),
  onlineTimeoutMs = ONLINE_TIMEOUT,
  offlineTimeoutMs = DEFAULT_OFFLINE_TIMEOUT
) {
  const map = new Map();
  (members || []).forEach((member) => {
    const id = member.device_unique_id || member.name;
    if (!id) return;
    map.set(id, getMemberPresenceStatus(member, now, onlineTimeoutMs, offlineTimeoutMs));
  });
  return map;
}

export function diffPresenceTransitions(currentMap, previousMap) {
  const toOnline = new Set();
  const toTempOffline = new Set();
  const toOffline = new Set();
  const prev = previousMap || new Map();
  const curr = currentMap || new Map();

  curr.forEach((status, id) => {
    const before = prev.get(id);
    if (before === status) return;
    if (status === PRESENCE_STATUS.ONLINE && before !== PRESENCE_STATUS.ONLINE) toOnline.add(id);
    if (status === PRESENCE_STATUS.TEMP_OFFLINE && before !== PRESENCE_STATUS.TEMP_OFFLINE) {
      // 仅从在线进入暂时离线时提示，避免灰→橙抖动
      if (before === PRESENCE_STATUS.ONLINE || before == null) toTempOffline.add(id);
    }
    if (status === PRESENCE_STATUS.OFFLINE && before !== PRESENCE_STATUS.OFFLINE) {
      if (before === PRESENCE_STATUS.ONLINE || before === PRESENCE_STATUS.TEMP_OFFLINE) toOffline.add(id);
    }
  });

  return { toOnline, toTempOffline, toOffline };
}

export function diffNewlyOnlineMembers(currentOnlineIds, previousOnlineIds) {
  const newly = new Set();
  currentOnlineIds.forEach((id) => {
    if (!previousOnlineIds.has(id)) newly.add(id);
  });
  return newly;
}

export function diffNewlyOfflineMembers(currentOnlineIds, previousOnlineIds) {
  const newly = new Set();
  (previousOnlineIds || new Set()).forEach((id) => {
    if (!currentOnlineIds.has(id)) newly.add(id);
  });
  return newly;
}

export function buildPresenceNotifications(members, memberIds, type = 'online') {
  const notifications = [];
  const orgToOpen = {};
  const groupToOpen = {};
  const memberToOpen = {};

  memberIds.forEach((memberId) => {
    const member = (members || []).find((m) => (m.device_unique_id || m.name) === memberId);
    if (!member) return;
    notifications.push({
      id: `${type}-${Date.now()}-${Math.random()}`,
      type, // online | temp_offline | offline
      memberId: member.device_unique_id || member.name,
      memberName: member.name || '未知队员',
      groupName: member.group_name || '未知小组',
      orgName: member.org_name || '未知组织',
      timestamp: new Date().toLocaleTimeString()
    });
    orgToOpen[member.org_name || '未知组织'] = true;
    groupToOpen[member.group_name || '未知小组'] = true;
    memberToOpen[getMemberExpandKey(member)] = true;
  });

  return { notifications, orgToOpen, groupToOpen, memberToOpen };
}

/** @deprecated 使用 buildPresenceNotifications(..., 'online') */
export function buildOnlineNotifications(members, newlyOnlineIds) {
  return buildPresenceNotifications(members, newlyOnlineIds, 'online');
}

/** 组织下全部展开/收起小组（及队员详情） */
export function computeOrgGroupsExpandedState(prev, groupsMap, expand) {
  const groupNames = Object.keys(groupsMap || {});
  const openGroup = { ...prev.openGroup };
  groupNames.forEach((g) => { openGroup[g] = expand; });

  let openMember = prev.openMember;
  let highlightedMemberIds = prev.highlightedMemberIds;
  if (!expand) {
    openMember = { ...prev.openMember };
    highlightedMemberIds = new Set(prev.highlightedMemberIds);
    groupNames.forEach((g) => {
      (groupsMap[g] || []).forEach((m) => {
        openMember[getMemberExpandKey(m)] = false;
        highlightedMemberIds.delete(m.device_unique_id || m.name);
      });
    });
  }
  return { openGroup, openMember, highlightedMemberIds };
}

/** 组织下展开在线队员 */
export function computeExpandOnlineInOrgState(prev, groupsMap) {
  const groupNames = Object.keys(groupsMap || {});
  const openGroup = { ...prev.openGroup };
  const openMember = { ...prev.openMember };
  const highlightedMemberIds = new Set(prev.highlightedMemberIds);

  groupNames.forEach((g) => {
    openGroup[g] = true;
    (groupsMap[g] || []).forEach((m) => {
      const online = isMemberOnlineNow(m);
      openMember[getMemberExpandKey(m)] = online;
      const id = m.device_unique_id || m.name;
      if (online) highlightedMemberIds.add(id);
      else highlightedMemberIds.delete(id);
    });
  });

  return { openGroup, openMember, highlightedMemberIds };
}

export function computeGroupMembersExpandedState(prev, membersList, expand) {
  const openMember = { ...prev.openMember };
  const highlightedMemberIds = new Set(prev.highlightedMemberIds);
  (membersList || []).forEach((m) => {
    openMember[getMemberExpandKey(m)] = expand;
    const id = m.device_unique_id || m.name;
    if (expand) highlightedMemberIds.add(id);
    else highlightedMemberIds.delete(id);
  });
  return { openMember, highlightedMemberIds };
}

export function computeExpandOnlineInGroupState(prev, membersList) {
  const openMember = { ...prev.openMember };
  const highlightedMemberIds = new Set(prev.highlightedMemberIds);
  (membersList || []).forEach((m) => {
    const online = isMemberOnlineNow(m);
    openMember[getMemberExpandKey(m)] = online;
    const id = m.device_unique_id || m.name;
    if (online) highlightedMemberIds.add(id);
    else highlightedMemberIds.delete(id);
  });
  return { openMember, highlightedMemberIds };
}
